<script setup lang="ts">
import axios from 'axios'
/** Partoo SDK instance returned by Partoo.init() - see https://developers.partoo.co/guides/sdk/getting-started/quickstart */
interface PartooPageInstance {
  login: (token: string) => void
  navigate: (page: string) => void
  back: () => void
  forward: () => void
  destroy: () => void
}

declare global {
  interface Window {
    Partoo: {
      init: (containerId: string, options: Record<string, unknown>) => PartooPageInstance
    }
  }
}

const props = withDefaults(
  defineProps<{
    page?:
      | 'businesses'
      | 'reviewManagement'
      | 'reviewAnalytics'
      | 'partnerConnections'
      | 'reviewAutoReplyWithoutComments'
      | 'reviewAutoReplyWithComments'
      | 'posts'
      | 'analytics'
      | 'presenceManagement'
    /** Height class for the iframe container. Override when the page adds chrome (e.g. a back header) above the iframe. */
    containerClass?: string
  }>(),
  {
    page: 'partnerConnections',
    containerClass: 'h-screen!'
  }
)

const partooPage = ref<PartooPageInstance | null>(null)

/** Wait for Partoo SDK to be available (script is loaded in index.html head). */
const waitForPartoo = (): Promise<void> => {
  if (typeof window.Partoo !== 'undefined') return Promise.resolve()
  return new Promise((resolve) => {
    const check = () => {
      if (typeof window.Partoo !== 'undefined') {
        resolve()
        return
      }
      setTimeout(check, 50)
    }
    check()
  })
}

const init = async () => {
  await waitForPartoo()

  if (partooPage.value) {
    // Already initialized: only navigate to the new page
    partooPage.value.navigate(props.page)
    return
  }

  const { data } = await axios.get('/review-managers/partoo/token')
  const token = data.data.token

  const options = {
    startPage: props.page,
    displayUserParams: false,
    displayAddButton: false,
    displayIntercom: false
  }

  if (window.Partoo) {
    const instance = window.Partoo.init('partoo-iframe-container', options)
    instance.login(token)
    partooPage.value = instance
  }
}

onMounted(init)

watch(
  () => props.page,
  (newPage) => {
    if (partooPage.value) {
      partooPage.value.navigate(newPage)
    }
  }
)

onUnmounted(() => {
  partooPage.value?.destroy?.()
  partooPage.value = null
})
</script>

<template>
  <div id="partoo-iframe-container" :class="containerClass"></div>
</template>
